/* VYAGO interaction layer.

   Everything here is progressive enhancement: each block checks for its own
   markup and exits quietly if the section is not on the page. Nothing in here
   is required for the page to render or for the content to be readable.

   Flavour data is handed over from Liquid in a #vyago-flavours JSON tag so the
   colours stay in the theme editor rather than being duplicated in JS.
*/
(() => {
  'use strict';

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- flavour data ---------------------------------------------------- */

  let FLAVOURS = {};
  try {
    const tag = document.getElementById('vyago-flavours');
    if (tag) FLAVOURS = JSON.parse(tag.textContent);
  } catch (e) {
    /* falls through to an empty map — sections degrade to their static state */
  }

  const gradient = (key, angle) =>
    FLAVOURS[key] ? `linear-gradient(${angle || '92deg'}, ${FLAVOURS[key].stops.join(', ')})` : '';

  const wash = (key) => {
    const f = FLAVOURS[key];
    if (!f) return '';
    return `radial-gradient(75% 90% at 82% 45%, ${f.stops[0]}2e 0%, ${f.accent}24 45%, transparent 72%)`;
  };

  /* ---- header height ---------------------------------------------------- */

  // --vy-header-h is written as 8rem so the CSS works on its own, but Dawn's
  // header is whatever the merchant's logo, menu and announcement bar add up
  // to. The hero's height and every sticky offset are measured off this, so it
  // is corrected from the real header once the page is up, and again on
  // resize. Nothing breaks if the header is not found; the 8rem stands.
  function syncHeaderHeight() {
    const header =
      document.querySelector('.shopify-section-header-sticky') ||
      document.querySelector('#shopify-section-header') ||
      document.querySelector('.section-header') ||
      document.querySelector('header');
    if (!header) return;
    const h = Math.round(header.getBoundingClientRect().height);
    if (h > 0 && h < 400) {
      document.documentElement.style.setProperty('--vy-header-h', h + 'px');
    }
  }

  let resizeTimer;
  addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(syncHeaderHeight, 150);
  });

  /* ---- hero ------------------------------------------------------------ */

  function initHero(root) {
    const can = root.querySelector('vyago-can');
    const washEl = root.querySelector('[data-vy-wash]');
    const word = root.querySelector('[data-vy-gradient-word]');
    const chips = Array.from(root.querySelectorAll('[data-vy-chip]'));
    if (!chips.length) return;

    const paint = (key) => {
      if (washEl) washEl.style.background = wash(key);
      if (word) word.style.backgroundImage = gradient(key);
    };

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const key = chip.getAttribute('data-vy-chip');
        chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
        paint(key);
        if (can) can.flavour = key;
      });
    });

    const active = chips.find((c) => c.getAttribute('aria-pressed') === 'true') || chips[0];
    paint(active.getAttribute('data-vy-chip'));
  }

  /* ---- what's inside: scroll-linked can rotation ------------------------ */

  function initInside(root) {
    const can = root.querySelector('vyago-can');
    const steps = Array.from(root.querySelectorAll('[data-vy-panel]'));
    if (!steps.length) return;

    const setActive = (el) => {
      steps.forEach((s) => s.toggleAttribute('data-active', s === el));
      if (can) can.showPanel(el.getAttribute('data-vy-panel'));
    };

    setActive(steps[0]);
    if (reduced) {
      steps.forEach((s) => s.setAttribute('data-active', ''));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (hit) setActive(hit.target);
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: [0, 0.5, 1] }
    );
    steps.forEach((s) => io.observe(s));
  }

  /* ---- product page ----------------------------------------------------- */

  function initProduct(root) {
    const can = root.querySelector('vyago-can');
    const washEl = root.querySelector('[data-vy-wash]');
    const key = root.getAttribute('data-vy-flavour');
    if (washEl && key) washEl.style.background = wash(key);

    const panelBtn = root.querySelector('[data-vy-panel-toggle]');
    if (panelBtn && can) {
      let open = false;
      panelBtn.addEventListener('click', () => {
        open = !open;
        can.showPanel(open ? 'nutrition' : 'front');
        panelBtn.textContent = open
          ? panelBtn.getAttribute('data-label-back')
          : panelBtn.getAttribute('data-label-open');
      });
    }

    // quantity stepper — keeps Dawn's quantity input working without its JS
    const qty = root.querySelector('[data-vy-qty]');
    if (qty) {
      const input = qty.querySelector('input');
      qty.addEventListener('click', (e) => {
        const step = e.target.closest('[data-vy-step]');
        if (!step || !input) return;
        const dir = step.getAttribute('data-vy-step') === 'up' ? 1 : -1;
        const min = parseInt(input.min || '1', 10);
        input.value = Math.max(min, (parseInt(input.value, 10) || min) + dir);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  /* ---- Klaviyo subscribe ------------------------------------------------ */

  /* Both signup forms post straight to Klaviyo's client-side subscribe
     endpoint rather than to Shopify, so each one can stamp its own source on
     the profile and land in the same list. The public company id is safe in
     the page: it is the site id Klaviyo's own onsite script already exposes.

     If the company id or list id are not set in theme settings the handler
     stands aside and Shopify's customer form posts as it always did, so the
     site never silently stops collecting addresses. */

  const KLAVIYO_REVISION = '2026-10-15';

  function subscribeToKlaviyo(root, email) {
    const company = root.dataset.klaviyoCompany;
    const list = root.dataset.klaviyoList;
    const source = root.dataset.source || 'Website';

    return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: source,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: { 'Signup Source': source },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: list } },
          },
        },
      }),
    }).then((res) => {
      // 202 is the documented success. Anything else is a real failure.
      if (!res.ok) throw new Error(`Klaviyo responded ${res.status}`);
      return true;
    });
  }

  function revealCode(root) {
    const box = root.querySelector('[data-vy-code]');
    if (!box) return;
    box.hidden = false;
    wireCopy(box);
  }

  function wireCopy(box) {
    if (box.dataset.vyCopyBound) return;
    box.dataset.vyCopyBound = '1';

    const copy = box.querySelector('[data-vy-code-copy]');
    if (!copy) return;

    let revert = 0;
    // The whole block is the target, not just the glyphs of the code, and the
    // confirmation is a class rather than a text swap so the two labels are
    // plain markup that CSS shows and hides.
    box.addEventListener('click', () => {
      const code = copy.dataset.code || copy.textContent.trim();

      box.classList.add('is-copied');
      window.clearTimeout(revert);
      revert = window.setTimeout(() => { box.classList.remove('is-copied'); }, 2000);

      // Best effort: some browsers refuse the write outside a trusted gesture,
      // and the code is on screen to select by hand either way.
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).catch(() => {});
        return;
      }
      const t = document.createElement('textarea');
      t.value = code;
      t.style.cssText = 'position:fixed;top:0;opacity:0';
      document.body.appendChild(t);
      t.select();
      try { document.execCommand('copy'); } catch (e) { /* nothing more to try */ }
      t.remove();
    });
  }

  function initCodeBox(box) {
    // a code box that is already visible (the Shopify fallback reload) still
    // needs its copy button wired up
    if (!box.hidden) wireCopy(box);
  }

  function initSubscribe(root) {
    if (!root.dataset.klaviyoCompany || !root.dataset.klaviyoList) return;

    const form = root.querySelector('form');
    const input = root.querySelector('input[type="email"]');
    // Only the form and the email field are genuinely required. Everything
    // else is built or worked around, because bailing out here means the
    // Shopify form posts and the page navigates, which is far worse than a
    // missing label.
    if (!form || !input) return;

    const button = root.querySelector('button[type="submit"]') || root.querySelector('button');

    let note = root.querySelector('[data-vy-subscribe-note]');
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-vy-subscribe-note', '');
      note.hidden = true;
      form.insertAdjacentElement('afterend', note);
    }

    const say = (text, isError) => {
      note.textContent = text;
      note.hidden = false;
      note.setAttribute('role', isError ? 'alert' : 'status');
      note.classList.toggle('is-error', !!isError);
    };

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = (input.value || '').trim();
      if (!email || !input.checkValidity()) { input.reportValidity(); return; }
      if (root.dataset.busy) return;

      root.dataset.busy = '1';
      const label = button ? button.textContent : '';
      if (button) {
        button.disabled = true;
        button.textContent = root.dataset.busyLabel || 'Joining...';
      }

      subscribeToKlaviyo(root, email)
        .then(() => {
          // put the label back before locking the form: leaving it on the busy
          // text makes a finished form look like it is still working
          if (button) button.textContent = label;
          // Lock the form, but not the copy button: it only appears at this
          // point and disabling it makes the code impossible to take.
          root.querySelectorAll('input, button').forEach((el) => {
            if (el.hasAttribute('data-vy-code-copy')) return;
            el.disabled = true;
          });
          say(root.dataset.successLabel || 'You are on the list.', false);
          input.value = '';
          revealCode(root);
          // Signing up through the popup stops it asking again this session.
          // Signing up through the footer form must not, or the popup would
          // be silenced by a form the visitor may never have seen.
          if (root.closest('[data-vy-popup]')) {
            try { sessionStorage.setItem('vyago-email-dismissed', '1'); } catch (e) { /* private mode */ }
          }
        })
        .catch(() => {
          say(root.dataset.errorLabel || 'That did not go through. Please try again.', true);
          if (button) { button.disabled = false; button.textContent = label; }
        })
        .finally(() => { delete root.dataset.busy; });
    });
  }

  /* ---- email capture ---------------------------------------------------- */

  function initCapture(root) {
    // Shopify posts the customer form and returns with ?customer_posted=true.
    // Dawn surfaces that; we just make sure the success state is announced.
    const success = root.querySelector('[data-vy-capture-success]');
    if (success) success.setAttribute('role', 'status');
  }

  /* ---- caffeine notice --------------------------------------------------- */

  function initCaffeine(root) {
    const KEY = 'vyago-caffeine-dismissed';
    let dismissed = false;
    try { dismissed = sessionStorage.getItem(KEY) === '1'; } catch (e) { /* private mode */ }
    if (dismissed) { root.hidden = true; return; }

    const close = root.querySelector('[data-vy-caffeine-close]');
    if (!close) return;
    close.addEventListener('click', () => {
      root.hidden = true;
      try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* private mode */ }
    });
  }

  /* ---- early access popup ------------------------------------------------ */

  function initPopup(root) {
    const KEY = 'vyago-email-dismissed';
    let dismissed = false;
    try { dismissed = sessionStorage.getItem(KEY) === '1'; } catch (e) { /* private mode */ }

    // If the visitor has just submitted the form, Shopify reloads the page with
    // ?customer_posted=true. Show the popup straight away so they see that it
    // worked rather than wondering whether it sent.
    const posted = root.querySelector('[data-vy-posted]');
    if (dismissed && !posted) return;

    const card = root.querySelector('.vy-popup__card');
    let shown = false;
    let lastFocus = null;

    const open = () => {
      if (shown) return;
      shown = true;
      lastFocus = document.activeElement;
      root.hidden = false;
      // let the element lay out before the transition class lands
      requestAnimationFrame(() => root.classList.add('is-open'));
      document.documentElement.style.overflow = 'hidden';
      const first = root.querySelector('input, button');
      if (first) first.focus({ preventScroll: true });
      document.addEventListener('keydown', onKey);
    };

    const close = () => {
      root.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* private mode */ }
      window.setTimeout(() => { root.hidden = true; }, 300);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    };

    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      // keep focus inside the dialog while it is open
      const f = card.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    root.querySelectorAll('[data-vy-popup-close]').forEach((el) => {
      el.addEventListener('click', close);
    });

    if (posted) { open(); return; }

    const delay = (parseInt(root.dataset.delay, 10) || 20) * 1000;
    window.setTimeout(open, delay);

    // Exit intent, held back for the first 8s so this can never read as a
    // page-load popup, and only when the pointer genuinely leaves the document.
    if (root.dataset.exitIntent === 'true') {
      let armed = false;
      window.setTimeout(() => { armed = true; }, 8000);
      document.documentElement.addEventListener('mouseleave', (e) => {
        if (armed && !e.relatedTarget && e.clientY <= 0) open();
      });
    }
  }

  /* ---- stockist locator filter ------------------------------------------ */

  function initLocator(root) {
    const input = root.querySelector('[data-vy-locator-search]');
    const items = Array.from(root.querySelectorAll('[data-vy-stockist]'));
    const empty = root.querySelector('[data-vy-locator-empty]');
    if (!input || !items.length) return;

    const run = () => {
      const q = input.value.trim().toLowerCase();
      let shown = 0;
      items.forEach((li) => {
        const hit = !q || li.getAttribute('data-vy-stockist').toLowerCase().includes(q);
        li.hidden = !hit;
        if (hit) shown++;
      });
      if (empty) empty.hidden = shown !== 0;
    };

    input.addEventListener('input', run);
    const form = input.closest('form');
    if (form) form.addEventListener('submit', (e) => { e.preventDefault(); run(); });
  }

  /* ---- boot -------------------------------------------------------------- */

  const INITS = {
    hero: initHero,
    inside: initInside,
    product: initProduct,
    capture: initCapture,
    locator: initLocator,
  };

  function bootStandalone(scope) {
    [['[data-vy-popup]', initPopup], ['[data-vy-caffeine]', initCaffeine], ['[data-vy-subscribe]', initSubscribe], ['[data-vy-code]', initCodeBox]].forEach(([sel, fn]) => {
      scope.querySelectorAll(sel).forEach((el) => {
        if (el.dataset.vyBooted) return;
        el.dataset.vyBooted = '1';
        try { fn(el); } catch (e) { /* never block the page on these */ }
      });
    });
  }

  function boot(scope) {
    Object.keys(INITS).forEach((name) => {
      scope.querySelectorAll(`[data-vy-init="${name}"]`).forEach((el) => {
        if (el.dataset.vyBooted) return;
        el.dataset.vyBooted = '1';
        try { INITS[name](el); } catch (e) { /* one section failing must not take the page down */ }
      });
    });
    bootStandalone(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { syncHeaderHeight(); boot(document); });
  } else {
    syncHeaderHeight();
    boot(document);
  }
  // the header can still be settling (logo image, announcement bar) at DOMContentLoaded
  addEventListener('load', syncHeaderHeight);

  // Theme editor: re-run when a section is re-rendered
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();